// Admin › Help: Scout help's sections, articles and revisions (vortex-api
// routes/adminHelp.ts). docs/wip/SCOUT_HELP.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { APP_NAMES, APP_PACKAGES, RULES, boldLabels, type CheckResult, type ChecklistItem, type HelpBlock, type NeedsSoftware, type SafetyKind } from '@vortex/help-render';

export type { CheckResult, Problem, ChecklistItem, HelpBlock, NeedsSoftware, SafetyKind } from '@vortex/help-render';

export type ArticleType = 'task' | 'troubleshooting' | 'faq' | 'known_issue' | 'video';
export type RevisionState = 'draft' | 'ready' | 'checked' | 'published';

export interface HelpSection {
  id: string;
  slug: string;
  title: string;
  /** The on-device design system's icon name. */
  icon: string;
  position: number;
}

export interface HelpRevisionSummary {
  id: string;
  articleId: string;
  number: number;
  title: string;
  state: RevisionState;
  authorName: string;
  claudeDraft: boolean;
  createdAt: string;
}

/** One line of the Articles list. */
export interface HelpArticleRow {
  id: string;
  topicId: string;
  sectionId: string;
  slug: string;
  type: ArticleType;
  liveRevisionId: string | null;
  redirectTo: string | null;
  previousPaths: string[];
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
  latest: HelpRevisionSummary;
  liveNumber: number | null;
  openFlags: number;
}

export const TYPE_LABELS: Record<ArticleType, string> = {
  task: 'Task',
  troubleshooting: 'Troubleshooting',
  faq: 'Common question',
  known_issue: 'Known issue',
  video: 'Video',
};

export const STATE_LABELS: Record<RevisionState, string> = {
  draft: 'Draft',
  ready: 'Ready for check',
  checked: 'Checked',
  published: 'Published',
};

/**
 * The spec's first cut of sections, in order, with the on-device design
 * system's icon for each (as on the Scout mock).
 */
export const FIRST_SECTIONS: Omit<HelpSection, 'id' | 'position'>[] = [
  { slug: 'getting-started', title: 'Getting started', icon: 'power' },
  { slug: 'home-and-settings', title: 'Home and settings', icon: 'layout-grid' },
  { slug: 'connect-your-boat', title: 'Connect your boat', icon: 'radio' },
  { slug: 'navigation', title: 'Navigation', icon: 'navigation' },
  { slug: 'true-helm', title: 'True Helm', icon: 'gauge' },
  { slug: 'anchor', title: 'Anchor', icon: 'anchor' },
  { slug: 'tides', title: 'Tides', icon: 'waves' },
  { slug: 'troubleshooting', title: 'Troubleshooting', icon: 'wrench' },
  { slug: 'common-questions', title: 'Common questions', icon: 'circle-question-mark' },
  { slug: 'videos', title: 'Videos', icon: 'video' },
];

export const TOPIC_ID = /^[A-Z]{2,5}-\d{2,3}$/;

/** "Set an anchor watch" → "set-an-anchor-watch", as the API accepts it. */
export const toSlug = (title: string) =>
  title.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 120);

export function useHelpSections() {
  return useQuery<HelpSection[]>({
    queryKey: ['help', 'sections'],
    queryFn: () => api.adminHelpSections(),
    staleTime: 60_000,
  });
}

export function useHelpArticles() {
  return useQuery<HelpArticleRow[]>({
    queryKey: ['help', 'articles'],
    queryFn: () => api.adminHelpArticles(),
    staleTime: 30_000,
  });
}

/** Adds the spec's first-cut sections that are missing, one by one in order. */
export function useAddFirstSections() {
  const qc = useQueryClient();
  return useMutation<void, Error, HelpSection[]>({
    mutationFn: async (existing) => {
      const have = new Set(existing.map((s) => s.slug));
      for (const s of FIRST_SECTIONS) if (!have.has(s.slug)) await api.adminHelpCreateSection(s);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['help', 'sections'] }),
  });
}

export interface NewArticleInput {
  topicId: string;
  sectionId: string;
  slug: string;
  type: ArticleType;
  revision: { title: string; summary: string; body: [] };
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation<{ article: { id: string } }, Error, NewArticleInput>({
    mutationFn: (body) => api.adminHelpCreateArticle(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['help', 'articles'] }),
  });
}

// ---- One article, in the editor ----

/** The fields one save holds, as the editor sends them (rows are Doc IDs). */
export interface RevisionBody {
  basedOn: string;
  title: string;
  summary: string;
  whereToFind: string | null;
  requirements: string | null;
  body: HelpBlock[];
  safety: SafetyKind | null;
  keywords: string[];
  needsSoftware: NeedsSoftware[];
  plans: string[];
  related: string[];
  seenLabels: string[];
  rows: string[];
}

export interface HelpRevision extends HelpRevisionSummary {
  summary: string;
  whereToFind: string | null;
  requirements: string | null;
  body: HelpBlock[];
  safety: SafetyKind | null;
  keywords: string[];
  needsSoftware: NeedsSoftware[];
  plans: string[];
  related: string[];
  seenLabels: string[];
  basedOn: string | null;
  authorId: string | null;
  rows: { docId: string; rowText: Record<string, string> }[];
}

export interface HelpDeviceCheck {
  id: string;
  revisionId: string;
  deviceName: string;
  software: Record<string, string>;
  result: 'pass' | 'fail';
  failedStep: number | null;
  note: string | null;
  checkedByName: string;
  createdAt: string;
}

export type FlagKind = 'row_changed' | 'row_retired' | 'stale_image' | 'fix_shipped' | 'reader_report';

/** Something to look at on an article (vortex-api help_flags). */
export interface HelpFlag {
  id: string;
  articleId: string;
  kind: FlagKind;
  key: string;
  /** row_changed: { docId, before, after, module?, version?, acceptedBy? }; row_retired: { docId, before }. */
  detail: Record<string, unknown>;
  status: 'open' | 'resolved' | 'no_change';
  reason: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export function useResolveFlag(articleId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; status: 'resolved' | 'no_change'; reason: string | null }>({
    mutationFn: ({ id, status, reason }) => api.adminHelpResolveFlag(id, { status, reason }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['help', 'article', articleId] });
      qc.invalidateQueries({ queryKey: ['help', 'articles'] });
    },
  });
}

export interface HelpArticleDetail {
  article: Omit<HelpArticleRow, 'latest' | 'liveNumber' | 'openFlags'>;
  revisions: HelpRevisionSummary[];
  revision: HelpRevision | null;
  checks: CheckResult[];
  passes: boolean;
  checklist: ChecklistItem[];
  deviceChecks: HelpDeviceCheck[];
  /** Open ones first, then the most recently closed. */
  flags: HelpFlag[];
}

export interface HelpMedia {
  id: string;
  kind: 'image' | 'clip' | 'video';
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationS: number | null;
  alt: string;
  noPersonalData: boolean;
  takenOn: { app: string; version: string } | null;
  captionsKey: string | null;
  posterKey: string | null;
  createdAt: string;
}

/** The OTA registry's apps, which Needs software names by key. */
export const APPS: { key: string; name: string }[] = Object.entries(APP_NAMES).map(([key, name]) => ({ key, name }));

export function useHelpArticle(id: string | undefined, revision?: string) {
  return useQuery<HelpArticleDetail>({
    queryKey: ['help', 'article', id, revision ?? 'newest'],
    queryFn: () => api.adminHelpArticle(id!, revision),
    enabled: !!id,
  });
}

export function useHelpMedia() {
  return useQuery<HelpMedia[]>({
    queryKey: ['help', 'media'],
    queryFn: () => api.adminHelpMedia(),
    staleTime: 30_000,
  });
}

export interface MediaUpload {
  kind: 'image' | 'clip';
  file: File;
  /** A clip's still frame, shown when reduced motion is asked for. */
  still: Blob | undefined;
  alt: string;
  noPersonalData: boolean;
  takenOn: { app: string; version: string } | null;
}

export type MediaPatch = Partial<Pick<HelpMedia, 'alt' | 'noPersonalData' | 'takenOn'>>;

/** What the API takes (vortex-api lib/helpMedia.ts MAX_BYTES; a clip's limit is rules.json clipMaxBytes); the API checks again. */
export const MEDIA_LIMITS = { image: 5_000_000, clip: RULES.clipMaxBytes, still: 2_000_000 } as const;

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation<{ media: HelpMedia; reused: boolean }, Error, MediaUpload>({
    mutationFn: (u) => api.adminHelpUploadMedia(u),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['help', 'media'] }),
  });
}

export function useUpdateMedia() {
  const qc = useQueryClient();
  return useMutation<HelpMedia, Error, { id: string; patch: MediaPatch }>({
    mutationFn: ({ id, patch }) => api.adminHelpUpdateMedia(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['help', 'media'] }),
  });
}

export function useSaveRevision(articleId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ id: string; number: number }, Error, RevisionBody>({
    mutationFn: (body) => api.adminHelpSaveRevision(articleId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['help', 'article', articleId] });
      qc.invalidateQueries({ queryKey: ['help', 'articles'] });
    },
  });
}

export function useMarkReady(articleId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (revisionId) => api.adminHelpMarkReady(revisionId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['help', 'article', articleId] });
      qc.invalidateQueries({ queryKey: ['help', 'articles'] });
    },
  });
}

export interface DeviceCheckBody {
  deviceId: string;
  result: 'pass' | 'fail';
  /** ChecklistItem keys. */
  ticks: string[];
  failedStep: number | null;
  note: string | null;
}

export function useDeviceCheck(articleId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ result: 'pass' | 'fail'; deviceName: string }, Error, { revisionId: string; body: DeviceCheckBody }>({
    mutationFn: ({ revisionId, body }) => api.adminHelpDeviceCheck(revisionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['help', 'article', articleId] });
      qc.invalidateQueries({ queryKey: ['help', 'articles'] });
    },
  });
}

const APP_BY_PACKAGE = new Map(Object.entries(APP_PACKAGES).map(([key, pkg]) => [pkg, key]));

/** An update check's app_details as { [app key]: installed version }: what the API records with a device check. */
export function softwareOf(appDetails: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of (Array.isArray(appDetails) ? appDetails : []) as Record<string, unknown>[]) {
    const key = APP_BY_PACKAGE.get(String(a?.package));
    if (key && typeof a.installed_version === 'string') out[key] = a.installed_version;
  }
  return out;
}

/** True when `installed` is `version` or later, by its numbers ("3.92.1-debug" reads as 3.92.1). */
export function atLeast(installed: string, version: string): boolean {
  const nums = (v: string) => (v.match(/^\d+(\.\d+)*/)?.[0] ?? '').split('.').map(Number);
  const a = nums(installed);
  const b = nums(version);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] || 0) - (b[i] || 0);
    if (d) return d > 0;
  }
  return true;
}

/** The checklist as the device check shows it: each step with the labels first written in it; other labels alone. */
export function checklistGroups(items: ChecklistItem[]): { item: ChecklistItem; labels: ChecklistItem[] }[] {
  const out: { item: ChecklistItem; labels: ChecklistItem[] }[] = [];
  // checklist() lists a step's new labels straight after it, so a label joins
  // the step before it only while nothing else has come between.
  let step: { labels: ChecklistItem[]; own: Set<string> } | null = null;
  for (const item of items) {
    if (item.kind === 'step') {
      const group = { item, labels: [] as ChecklistItem[] };
      step = { labels: group.labels, own: new Set(boldLabels(item.text)) };
      out.push(group);
    } else if (step?.own.has(item.text)) {
      step.labels.push(item);
    } else {
      step = null;
      out.push({ item, labels: [] });
    }
  }
  return out;
}

/** The API's error message, for a 4xx the page can show as is. */
export function errorText(err: unknown): string {
  const data = (err as { response?: { data?: { error?: string; details?: { message?: string }[] } } })?.response?.data;
  const detail = data?.details?.[0]?.message;
  return data?.error ? (data.error === 'Validation failed' && detail ? detail : data.error) : 'Something went wrong. Try again.';
}
